import { Component, computed, EventEmitter, inject, Input, Output } from "@angular/core";
import { View as ViewEnum } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { GranularityFilter } from "@bitwarden/web-vault/app/gloss/views/views-control/views-customize/granularity.filter";
import {
  dayGranularity,
  weekGranularity,
  monthGranularity,
  quarterGranularity,
  yearGranularity,
  GranularityProperty,
} from "@bitwarden/web-vault/app/models/types/balanceGroupingTypes";
import { BreakpointService } from "@bitwarden/web-vault/app/shared/breakpoints";
import { DropdownSelectMapOption } from "@bitwarden/web-vault/app/directives/dropdown.directive";
import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";
import { relativeDateLabel } from "@bitwarden/web-vault/app/gloss/views/views-control/views-datepicker/datepicker-utils";

const GRANULARITY_LABELS: { [p: string]: string } = {
  [dayGranularity]: "daily",
  [weekGranularity]: "weekly",
  [monthGranularity]: "monthly",
  [quarterGranularity]: "quarterly",
  [yearGranularity]: "yearly",
};

const SHORT_GRANULARITY_LABELS: { [p: string]: string } = {
  [dayGranularity]: "Dly",
  [weekGranularity]: "Wly",
  [monthGranularity]: "Mly",
  [quarterGranularity]: "Qly",
  [yearGranularity]: "Yly",
};

@Component({
  selector: "app-views-control",
  templateUrl: "./views-control.component.html",
})
export class ViewsControlComponent {
  @Input() currentView: ViewEnum;
  /** The user picked a different granularity in the dropdown. */
  @Output() granularityPicked = new EventEmitter<void>();
  protected ViewEnum = ViewEnum;
  protected granularityFilter = inject(GranularityFilter);
  private todayStore = inject(CalculationTodayStoreService);

  // Relative "time ago" label next to the today-view datepicker.
  protected todayRelativeLabel = computed(() =>
    relativeDateLabel(this.todayStore.period.selected().endDate),
  );
  isSmallScreen = inject(BreakpointService).isSmOrSmaller;

  granularityMapOptions = computed((): DropdownSelectMapOption[] => {
    const options = this.granularityFilter?.options() ?? [];
    return options.map((g: GranularityProperty) => ({
      realValue: g,
      optionDisplayValue: GRANULARITY_LABELS[g],
      selectedDisplayValue: this.isSmallScreen()
        ? SHORT_GRANULARITY_LABELS[g]
        : GRANULARITY_LABELS[g],
      enabled: true,
    }));
  });

  selectedGranularityMapValue = computed((): string => {
    return this.granularityFilter?.selectedGranularity() ?? "";
  });

  selectGranularityMapOption(granularity: string) {
    const mappedGranularity = granularity as GranularityProperty;
    this.granularityFilter.onGranularitySelection(mappedGranularity);
  }

  /** The dropdown's `optionSelected`, which only a real pick emits (unlike `selectGranularityMapOption`). */
  pickGranularity(granularity: string) {
    const changed = granularity !== this.selectedGranularityMapValue();
    this.selectGranularityMapOption(granularity);
    if (changed) {
      this.granularityPicked.emit();
    }
  }
}
